import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {
  API_TOKEN_SCOPES,
  API_TOKEN_SCOPE_LABELS,
  apiTokenInputSchema,
  type ApiTokenCreatedDto,
  type ApiTokenDto,
  type ApiTokenInput,
} from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { ConfirmDialog } from '../../components/common/ConfirmDialog'
import { EmptyState, ErrorAlert, LoadingBlock } from '../../components/common/Feedback'
import { PageHeader } from '../../components/common/PageHeader'
import { DateField, EntitySelect, EnumSelect } from '../../components/common/fields'
import { useUsers } from '../../hooks/useLookups'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { formatDate, formatDateTime } from '../../lib/format'
import { qk } from '../../lib/queryClient'

type FormValues = z.input<typeof apiTokenInputSchema>

const EMPTY: FormValues = { name: '', userId: '', scope: 'READ_ONLY', expiresAt: '' }

function CreateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (created: ApiTokenCreatedDto) => void
}) {
  const queryClient = useQueryClient()
  const users = useUsers()
  const form = useForm<FormValues, unknown, ApiTokenInput>({
    resolver: formResolver(apiTokenInputSchema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (open) form.reset(EMPTY)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const save = useMutation({
    mutationFn: (values: ApiTokenInput) => api.post<ApiTokenCreatedDto>('/api-tokens', values),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: qk.apiTokens })
      onCreated(result)
      onClose()
    },
  })

  const { errors } = form.formState

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>טוקן חדש</DialogTitle>
        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="שם הטוקן"
              required
              autoFocus
              {...form.register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message ?? 'למה הוא משמש, כדי שאפשר יהיה לבטל את הנכון'}
            />
            <EntitySelect
              control={form.control}
              name="userId"
              label="פועל בזהות של"
              options={(users.data ?? []).map((u) => ({
                id: u.id,
                label: u.name + ' · ' + u.email,
              }))}
              loading={users.isPending}
              helperText="כל פעולה תיוחס למשתמש הזה ביומן השינויים"
            />
            <EnumSelect
              control={form.control}
              name="scope"
              label="הרשאה"
              options={API_TOKEN_SCOPES.map((s) => ({ id: s, label: API_TOKEN_SCOPE_LABELS[s] }))}
            />
            <DateField control={form.control} name="expiresAt" label="תפוגה (אופציונלי)" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            יצירה
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

/** The secret lives in the browser for this dialog's lifetime and nowhere else. */
function RevealDialog({
  created,
  onClose,
}: {
  created: ApiTokenCreatedDto | null
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    if (!created) return
    try {
      await navigator.clipboard.writeText(created.token)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Dialog open={Boolean(created)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>הטוקן נוצר</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>זו הפעם היחידה שהטוקן מוצג</AlertTitle>
          במסד נשמר רק גיבוב חד-כיווני שלו, ולכן אי אפשר לשחזר אותו. העתק אותו עכשיו למקום
          מאובטח. אם אבד — הנפק חדש ובטל את זה.
        </Alert>

        <Paper
          variant="outlined"
          sx={{ p: 1.5, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Box
            component="code"
            sx={{ flexGrow: 1, fontSize: '0.8125rem', wordBreak: 'break-all', direction: 'ltr' }}
          >
            {created?.token}
          </Box>
          <Tooltip title={copied ? 'הועתק' : 'העתקה'}>
            <Button onClick={() => void copy()} startIcon={<ContentCopyIcon />} size="small">
              {copied ? 'הועתק' : 'העתקה'}
            </Button>
          </Tooltip>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          כך שולחים אותו בכל בקשה:
        </Typography>
        <Box
          component="pre"
          sx={{
            mt: 0.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
            fontSize: '0.75rem',
            overflowX: 'auto',
            direction: 'ltr',
            m: 0,
          }}
        >
          Authorization: Bearer {created?.token}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          סיימתי להעתיק
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function StateChip({ token }: { token: ApiTokenDto }) {
  if (token.revokedAt) return <Chip size="small" label="בוטל" color="error" variant="outlined" />
  if (token.expiresAt && new Date(token.expiresAt).getTime() <= Date.now()) {
    return <Chip size="small" label="פג" color="warning" variant="outlined" />
  }
  return <Chip size="small" label="פעיל" color="success" variant="outlined" />
}

export function ApiTokensPage() {
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [created, setCreated] = useState<ApiTokenCreatedDto | null>(null)
  const [toRevoke, setToRevoke] = useState<ApiTokenDto | null>(null)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.apiTokens,
    queryFn: () => api.get<ApiTokenDto[]>('/api-tokens'),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.post<ApiTokenDto>(`/api-tokens/${id}/revoke`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.apiTokens })
      setToRevoke(null)
    },
  })

  return (
    <>
      <PageHeader
        title="טוקני API"
        subtitle="גישה מהמערכת שלכם למערכת אחרת — סוכן AI, סקריפט או אינטגרציה — בלי דפדפן"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            טוקן חדש
          </Button>
        }
      />

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}
      {revoke.error ? <ErrorAlert error={revoke.error} /> : null}
      {isPending ? <LoadingBlock /> : null}

      {data ? (
        data.length === 0 ? (
          <EmptyState
            title="אין טוקנים"
            hint="טוקן מאפשר למערכת חיצונית לקרוא ולכתוב דרך ה-API בלי התחברות בדפדפן"
          />
        ) : (
          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>שם</TableCell>
                  <TableCell>זהות</TableCell>
                  <TableCell>הרשאה</TableCell>
                  <TableCell>מצב</TableCell>
                  <TableCell>שימוש אחרון</TableCell>
                  <TableCell>תפוגה</TableCell>
                  <TableCell>נוצר על ידי</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((token) => (
                  <TableRow key={token.id} hover>
                    <TableCell>{token.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{token.userName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {token.userEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={token.scope === 'READ_WRITE' ? 'primary' : 'default'}
                        label={API_TOKEN_SCOPE_LABELS[token.scope]}
                      />
                    </TableCell>
                    <TableCell>
                      <StateChip token={token} />
                    </TableCell>
                    <TableCell>{token.lastUsedAt ? formatDateTime(token.lastUsedAt) : '—'}</TableCell>
                    <TableCell>{token.expiresAt ? formatDate(token.expiresAt) : '—'}</TableCell>
                    <TableCell>{token.createdByName}</TableCell>
                    <TableCell align="left">
                      {token.revokedAt ? null : (
                        <Button size="small" color="error" onClick={() => setToRevoke(token)}>
                          ביטול
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )
      ) : null}

      <Paper variant="outlined" sx={{ p: 2.5, mt: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          מה חשוב לדעת
        </Typography>
        <Stack spacing={1.25}>
          <Typography variant="body2">
            <strong>הטוקן פועל בזהות של משתמש.</strong> כל משימה שייצור וכל שינוי שיבצע ייוחסו
            לאותו משתמש ביומן השינויים. עדיף לפתוח משתמש נפרד לסוכן, כדי שאפשר יהיה להבדיל בין
            פעילות שלו לפעילות של אדם.
          </Typography>
          <Typography variant="body2">
            <strong>מסלולי ניהול חסומים לטוקנים.</strong> גם טוקן שפועל בזהות של אדמין לא יכול
            ליצור משתמשים, לנתק את Drive או להנפיק טוקנים. אלה הפעולות שאין מהן חזרה זולה.
          </Typography>
          <Typography variant="body2">
            <strong>קריאה בלבד היא ברירת המחדל.</strong> טוקן כזה נדחה בכל בקשה שאינה קריאה. כדאי
            להתחיל ממנה ולהרחיב רק כשהסוכן הוכיח את עצמו.
          </Typography>
          <Typography variant="body2">
            <strong>ביטול הוא מיידי ואינו מוחק.</strong> השורה נשארת, כדי שיומן השינויים ימשיך
            להצביע על משהו שקיים.
          </Typography>
        </Stack>
      </Paper>

      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={setCreated} />
      <RevealDialog created={created} onClose={() => setCreated(null)} />
      <ConfirmDialog
        open={Boolean(toRevoke)}
        title="ביטול טוקן"
        message={`הטוקן "${toRevoke?.name ?? ''}" יפסיק לעבוד מיד. אי אפשר להחזיר אותו — צריך להנפיק חדש.`}
        confirmLabel="ביטול הטוקן"
        busy={revoke.isPending}
        onConfirm={() => toRevoke && revoke.mutate(toRevoke.id)}
        onClose={() => setToRevoke(null)}
      />
    </>
  )
}
